import {
  Badge,
  Button,
  Grid,
  Icon,
  Message,
  Table,
} from "@intility/bifrost-react";
import { useDecideRequest, usePendingRequests } from "../../api/access";
import { PageLayout } from "../../components/layout/PageLayout";
import { formatDate } from "../../lib/clientTypes";

export default function Requests() {
  // No capability gating here: the pending endpoint already returns only
  // requests whose target the caller is maintainer on — anyone who sees a
  // row is allowed to decide it.
  const { data: requests, isLoading, error } = usePendingRequests();
  const decide = useDecideRequest();

  if (isLoading) {
    return (
      <div className="bf-content bfl-page-padding">
        <Grid
          gap={16}
          style={{
            minHeight: "200px",
            placeContent: "center",
            textAlign: "center",
          }}
        >
          <Icon.Spinner size={32} />
          <p className="bf-p">Loading requests...</p>
        </Grid>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bf-content bfl-page-padding">
        <Message state="warning" header="Failed to load requests">
          {error instanceof Error
            ? error.message
            : "An unexpected error occurred."}
        </Message>
      </div>
    );
  }

  return (
    <PageLayout
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Requests" }]}
      title="Access requests"
      description="Requests for client access to objects you administer. Approving writes the grant on your behalf."
    >
      <Grid gap={24}>
        {decide.error && (
          <Message state="warning" header="Action failed">
            {decide.error instanceof Error
              ? decide.error.message
              : "An unexpected error occurred."}
          </Message>
        )}

        {!requests || requests.length === 0 ? (
          <Message state="neutral">
            No pending requests need your approval.
          </Message>
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Client</Table.HeaderCell>
                <Table.HeaderCell>Target</Table.HeaderCell>
                <Table.HeaderCell>Role</Table.HeaderCell>
                <Table.HeaderCell>Requested</Table.HeaderCell>
                <Table.HeaderCell> </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {requests.map((r) => (
                <Table.Row key={r.id}>
                  <Table.Cell>{r.clientName}</Table.Cell>
                  <Table.Cell>
                    <code
                      style={{ fontFamily: "monospace", fontSize: "0.875em" }}
                    >
                      {r.target}
                    </code>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge state="neutral">{r.requestedRole}</Badge>
                  </Table.Cell>
                  <Table.Cell>{formatDate(r.requestedAt)}</Table.Cell>
                  <Table.Cell>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        small
                        disabled={decide.isPending}
                        onClick={() =>
                          decide.mutate({ id: r.id, decision: "approve" })
                        }
                      >
                        Approve
                      </Button>
                      <Button
                        small
                        variant="flat"
                        disabled={decide.isPending}
                        style={{ color: "var(--bfc-alert)" }}
                        onClick={() =>
                          decide.mutate({ id: r.id, decision: "deny" })
                        }
                      >
                        Deny
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Grid>
    </PageLayout>
  );
}
